/** Einstieg der PWA: Router, Layout, Sync-Status, Service Worker. */
import { actionQueue, currentMe, isAdmin, loadMe, logout, outbox, syncNow } from './store.js';
import { $, esc, toast } from './ui.js';
import { renderInvite, renderLogin } from './views/login.js';
import { refreshAppliancesBox, refreshVisitsBox, refreshWasteBox, renderHome, stopHomeTimer } from './views/home.js';
import { renderEntries, renderEntryForm } from './views/entries.js';
import { renderAbsences } from './views/absences.js';
import { renderCalendar } from './views/calendar.js';
import { renderWaste } from './views/waste.js';
import { renderAdmin } from './views/admin.js';
import { berlinDate, monthOf } from '../shared/time.js';

const view = () => $('#view');

function layout(): void {
  const admin = isAdmin();
  const me = currentMe();
  $('#app').innerHTML = `
    <header class="top">
      <a href="#/" class="brand">HubTime <span>Zeit</span></a>
      <button id="sync" class="sync" type="button" aria-live="polite"></button>
      <details class="menu"><summary aria-label="Menü">${esc(me?.user.short ?? '')}</summary>
        <div class="menu-pop">
          <p><strong>${esc(me?.user.name ?? '')}</strong></p>
          <a href="#/muell">Müllabfuhr</a>
          <button type="button" id="logout" class="linklike">Abmelden</button>
        </div></details>
    </header>
    <main id="view" tabindex="-1"></main>
    <nav class="bottom">
      <a href="#/" data-nav="home"><span class="ico">⏱</span>Heute</a>
      <a href="#/eintraege" data-nav="eintraege"><span class="ico">☰</span>Einträge</a>
      <a href="#/abwesenheit" data-nav="abwesenheit"><span class="ico">✈</span>Abwesend</a>
      <a href="#/kalender" data-nav="kalender"><span class="ico">▦</span>Team</a>
      ${admin ? '<a href="#/admin" data-nav="admin"><span class="ico">⚙</span>Verwaltung</a>' : ''}
    </nav>`;
  $('#logout').addEventListener('click', () => void logout());
  $('#sync').addEventListener('click', async () => {
    const r = await syncNow();
    if (r?.offline) toast('Keine Verbindung. Einträge bleiben sicher auf dem Handy gespeichert.', 'warn');
  });
  void updateSyncStatus();
}

async function updateSyncStatus(): Promise<void> {
  const el = document.getElementById('sync');
  if (!el) return;
  const pending = (await outbox.pendingCount()) + (await actionQueue.count());
  const failed = (await outbox.failed()).length;
  const online = navigator.onLine;
  let text: string;
  let cls: string;
  if (failed) {
    text = `${failed} ${failed === 1 ? 'Eintrag' : 'Einträge'} abgelehnt – bitte prüfen`;
    cls = 'err';
  } else if (pending) {
    text = `${pending} ${pending === 1 ? 'Eintrag wartet' : 'Einträge warten'} auf Upload${online ? '' : ' (offline)'}`;
    cls = 'wait';
  } else {
    text = online ? 'Alles gespeichert' : 'Offline';
    cls = online ? 'ok' : 'off';
  }
  el.textContent = text;
  el.className = `sync ${cls}`;
}

async function route(): Promise<void> {
  stopHomeTimer();
  const hash = location.hash.replace(/^#\/?/, '');
  const parts = hash.split('/').filter(Boolean).map(decodeURIComponent);
  const [page, ...rest] = parts;

  if (page === 'einladung' && rest[0]) {
    $('#app').innerHTML = '<main id="view"></main>';
    await renderInvite(view(), rest[0], boot);
    return;
  }
  if (!currentMe()) {
    $('#app').innerHTML = '<main id="view"></main>';
    renderLogin(view(), () => {
      location.hash = '#/';
      void boot();
    });
    return;
  }
  if (!document.getElementById('sync')) layout();
  document.querySelectorAll('nav.bottom a').forEach((a) => {
    const key = (a as HTMLElement).dataset.nav;
    a.classList.toggle('active', key === (page ?? 'home') || (key === 'eintraege' && page === 'eintrag'));
  });
  document.querySelector('details.menu')?.removeAttribute('open');
  const el = view();
  const thisMonth = monthOf(berlinDate(Date.now()));
  try {
    switch (page) {
      case undefined:
      case '':
        await renderHome(el);
        break;
      case 'eintraege':
        await renderEntries(el, rest[0] ?? thisMonth);
        break;
      case 'eintrag':
        await renderEntryForm(el, rest[0] === 'neu' ? null : rest[0]);
        break;
      case 'abwesenheit':
        await renderAbsences(el, Number(rest[0]) || Number(thisMonth.slice(0, 4)));
        break;
      case 'kalender':
        await renderCalendar(el, rest[0] ?? thisMonth);
        break;
      case 'muell':
        await renderWaste(el);
        break;
      case 'admin':
        if (!isAdmin()) {
          location.hash = '#/';
          return;
        }
        await renderAdmin(el, rest);
        break;
      default:
        el.innerHTML = '<p>Seite nicht gefunden.</p><a class="btn" href="#/">Zur Startseite</a>';
    }
  } catch (err) {
    el.innerHTML = `<div class="alert error">${esc((err as Error).message)}</div>`;
  }
  el.focus({ preventScroll: true });
}

async function boot(): Promise<void> {
  await loadMe();
  document.getElementById('sync')?.remove();
  await route();
  if (currentMe()) void syncNow();
}

window.addEventListener('hashchange', () => void route());
window.addEventListener('online', () => {
  void updateSyncStatus();
  void syncNow();
});
window.addEventListener('offline', () => void updateSyncStatus());
window.addEventListener('ze:synced', (ev) => {
  void updateSyncStatus();
  const r = (ev as CustomEvent).detail as { rejected: { error: string }[]; conflicts: unknown[]; warnings: { warnings: string[] }[] };
  if (r.rejected.length) toast(`Nicht gespeichert: ${r.rejected[0].error}`, 'error');
  else if (r.conflicts.length) toast('Ein Eintrag wurde zwischenzeitlich geändert – der neuere Stand bleibt erhalten. Bitte prüfen.', 'warn');
  else if (r.warnings.length) toast(`Hinweis: ${r.warnings[0].warnings[0]}`, 'warn');
  // Liste/Startseite nach Sync auffrischen (nicht in Formularen, um Eingaben nicht zu verlieren)
  const page = location.hash.replace(/^#\/?/, '').split('/')[0];
  if (page === '') void refreshVisitsBox();
  if ((r.rejected.length || r.conflicts.length) && (page === '' || page === 'eintraege')) void route();
});
window.addEventListener('ze:actions', (ev) => {
  void updateSyncStatus();
  const r = (ev as CustomEvent).detail as { ok: unknown[]; rejected: { error?: string }[] };
  if (r.rejected.length) toast(r.rejected[0].error ?? 'Aktion abgelehnt.', 'warn');
  if (r.ok.length || r.rejected.length) window.dispatchEvent(new CustomEvent('ze:shared-changed'));
});
window.addEventListener('ze:shared-changed', () => {
  const page = location.hash.replace(/^#\/?/, '').split('/')[0];
  if (page === '') {
    void refreshWasteBox();
    void refreshAppliancesBox();
  }
  else if (page === 'muell') void route();
});
window.addEventListener('ze:unauthorized', () => {
  if (currentMe()) {
    toast('Sitzung abgelaufen – bitte neu anmelden.', 'warn');
  }
});
outbox.onChange(() => void updateSyncStatus());
actionQueue.onChange(() => void updateSyncStatus());
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible' && currentMe()) void syncNow();
});
window.setInterval(() => {
  if (navigator.onLine && currentMe()) void syncNow();
}, 60_000);

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    const hadController = !!navigator.serviceWorker.controller;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (hadController) toast('Neue Version geladen – App bitte einmal schließen und neu öffnen.', 'warn');
    });
    navigator.serviceWorker.register('/sw.js').catch((err) => console.warn('Service Worker nicht registriert', err));
  });
}

void boot();
